import { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { useNavigate, useParams, Link } from "react-router-dom";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { Formik } from "formik";
import AppInputField from "components/shared/FormComponents/AppInputField";
import LoadingButton from "components/shared/LoadingButton";
import { CHATTER_TEMPLATE_LIST } from "config/paths";
import { successMessageToast } from "helpers/common";
import { CustomAsyncSelect, RequiredFieldAstrick } from "components/Common/Common";
import CKEditorField from "components/CkEditor/CkEditor";
import TherapistListing from "../Components/TherapistListing";
import validationSchema from "./validation.schema";
import CharacterCounter from "components/shared/CharacterCounter";
import { useCreateChatterTemplateMutation, useGetChatterTemplateQuery, useUpdateChatterTemplateMutation } from "store/services/chatterTemplate";
import FormError from "components/shared/FormComponents/FormError";
import { useLazyProgramLookupQuery } from "store/services/utility";

const TITLE_LENGTH = 80;

const AddChatterTemplate = () => {
    const { id } = useParams();
    const editMode = !!id;
    const navigate = useNavigate();
    const divisonId = ""
    const [selectedTherapists, setSelectedTherapists] = useState([]);

    const { data: templateData, isLoading: templateDataLoading, isError: templateDataError } = useGetChatterTemplateQuery({ id }, { skip: !id });
    const [createTemplate, { isLoading }] = useCreateChatterTemplateMutation();
    const [updateTemplate, { isLoading: updateTemplateLoading }] = useUpdateChatterTemplateMutation();

    // School Lookup
    const [getProgramList, { isFetching: programListFetching }] = useLazyProgramLookupQuery();

    const initialValues = useMemo(() => {
        return {
            program_id: templateData ? {
                value: templateData?.program_id,
                label: templateData?.program?.name,
            } : null,
            description: templateData?.description || "",
            assignTherapists: templateData?.chatterTemplateAssignedTherapists?.length
                ? templateData?.chatterTemplateAssignedTherapists?.map(item => item.therapistId)
                : []
        }
    }, [templateData]);

    useEffect(() => {
        setSelectedTherapists(initialValues?.assignTherapists);
    }, [initialValues?.assignTherapists])

    const handleClickCancel = () => {
        navigate(CHATTER_TEMPLATE_LIST);
    }

    const handleSubmit = async (values) => {
        try {
            const body = {
                program_id: values?.program_id?.value,
                description: values?.description,
                assignTherapists: selectedTherapists
            }
            let result;
            if (editMode) {
                result = await updateTemplate({
                  id,
                  body
                }).unwrap();
            } else {
                result = await createTemplate({ body }).unwrap();
            }

            successMessageToast(result.data?.message || "Chatter program description created successfully");
            navigate(CHATTER_TEMPLATE_LIST);
        } catch (err) {
            console.error("Error creating chatter template", err);
        }
    }

    const getProgramOptions = async (search) => {
        try {
            console.log("search", search)
            const data = await getProgramList(search).unwrap();
            return data?.map(i => ({ label: i.name, value: i.id }))
        } catch (error) {
            console.log("Error Getting school options");
        };
        };

    return (
        <>
            <section className={`${styles.addForm} position-relative py-4 addForm`}>
                <Container fluid={true}>
                    <Row>
                        <Col lg="12" className="my-2">
                            <div className="pageHead pb-3 d-flex align-items-center">
                                <Link
                                    className="themeBlue fw-sbold back-to-btn"
                                    to={CHATTER_TEMPLATE_LIST}
                                >
                                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                                    </svg> Back
                                </Link>
                                <h6 className="m-0 fw-bold themeBlue">
                                    CHATTER Program Description
                                </h6>
                            </div>
                        </Col>
                        <Col lg="12" className="my-2">
                            {templateDataLoading ? (
                                <LoadingIndicator />
                            ) : templateDataError ? (
                                <ErrorComponent />
                            ) : (
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema(TITLE_LENGTH)}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({
                                        values,
                                        errors,
                                        touched,
                                        handleSubmit,
                                        setFieldValue,
                                    }) => (
                                        <Form onSubmit={handleSubmit}>
                                            <div
                                                className="py-3"
                                                style={{ borderTop: "1px solid #41a2a84d" }}
                                            >
                                                <Row>
                                                    <Col md="4" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Name of Chatter Program
                                                            {/* <RequiredFieldAstrick /> */}
                                                        </label>
                                                        {/* <p className="text-muted"><em>Aim for 60 characters. The full name can be included in the description below.</em></p> */}
                                                    </Col>
                                                    <Col md="8" sm="12" className="mt-2 mb-5">
                                                        <div className="position-relative">
                                                            {/* <AppInputField
                                                                name="title"
                                                                placeholder="Enter Chatter Program Name"
                                                                className={`${styles.formControl} form-control`}
                                                                errorStyles={{ position: "absolute" }}
                                                            /> */}
                                                            <CustomAsyncSelect
                                                                // name={"title"}
                                                                placeholder="Choose a Program"
                                                                loadOptions={getProgramOptions}
                                                                onChange={(selected) => setFieldValue("program_id", selected)}
                                                                value={values?.program_id || null}
                                                                defaultOptions={true}
                                                                isFilter={true}
                                                                isLoading={programListFetching}
                                                                // isClearable
                                                            />
                                                            <div className="d-flex justify-content-end position-absolute end-0">
                                                                <CharacterCounter
                                                                    value={values?.title}
                                                                    maxLength={TITLE_LENGTH}
                                                                />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md="4" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Chatter Program Description
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <p className="text-muted"><em>This description will be used in the Chatter Program Summary Report.</em></p>
                                                    </Col>
                                                    <Col md="8" sm="12" className="my-2">
                                                        <div className={`ck-editor-wrapper small`} >
                                                            <CKEditorField
                                                                handleChange={(event, editor) => setFieldValue("description", editor?.getData())}
                                                                value={initialValues?.description}
                                                            />
                                                            {errors?.description ? (
                                                                <div className="error">{errors.description}</div>
                                                            ) : null}
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md="12" sm="12" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0 d-flex"
                                                        >
                                                            Assign to Therapists
                                                        </label>
                                                    </Col>
                                                    <Col md="12" sm="12" className="my-2">
                                                        <TherapistListing
                                                            selectedTherapists={selectedTherapists}
                                                            setSelectedTherapists={setSelectedTherapists}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                            <Row>
                                                <Col className="my-2" lg="12">
                                                    <FormError />
                                                </Col>
                                                <Col className="my-2" lg="12">
                                                    <div
                                                        className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                                                        style={{ borderTop: "1px solid #41a2a84d" }}
                                                    >
                                                        <LoadingButton
                                                            type="submit"
                                                            className="d-flex align-items-center justify-content-center commonBtn"
                                                            isLoading={isLoading || updateTemplateLoading}
                                                        >
                                                            Save
                                                        </LoadingButton>
                                                        <Button
                                                            onClick={handleClickCancel}
                                                            className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default AddChatterTemplate;